/**
 * Free, CORS-friendly translation service.
 *
 *  Tries multiple backends in order, validates each response shape,
 *  and only returns a result that:
 *    - is a non-empty string,
 *    - is not the literal string "string" (some hosts return their JSON
 *      schema as a dry-run / anti-abuse response - that has shown up
 *      from both lingva.ml and the MyMemory CDN under load),
 *    - is not byte-identical to the source (translation should produce
 *      *some* difference for any reasonable language pair).
 *
 *  Backends (best effort):
 *    1. lingva.lunar.icu      (Lingva / Google Translate)
 *    2. lingva.ml             (Lingva / Google Translate)
 *    3. api.mymemory.translated.net   (TMS, includes Google fallback)
 *
 *  Responses are cached per-session in a Map so repeated translations
 *  of the same source string never hit the network.
 */

const LINGVA_MIRRORS = [
  'https://lingva.lunar.icu',
  'https://lingva.ml',
]

const LINGVA_LANG = {
  en: 'en', fr: 'fr', es: 'es',
  'zh-CN': 'zh', 'zh-TW': 'zh_HANT',
  pa: 'pa', hi: 'hi', ar: 'ar', tl: 'tl',
  pt: 'pt', vi: 'vi', ko: 'ko',
  it: 'it', de: 'de', ru: 'ru',
}

const MYMEMORY_LANG = {
  en: 'en-US', fr: 'fr-FR', es: 'es-ES',
  'zh-CN': 'zh-CN', 'zh-TW': 'zh-TW',
  pa: 'pa-IN', hi: 'hi-IN', ar: 'ar-SA', tl: 'tl-PH',
  pt: 'pt-PT', vi: 'vi-VN', ko: 'ko-KR',
  it: 'it-IT', de: 'de-DE', ru: 'ru-RU',
}

const cache = new Map()
function cacheKey(text, source, target) { return `${source}|${target}|${text}` }

function isValidTranslation(translated, source) {
  if (typeof translated !== 'string') return false
  const t = translated.trim()
  if (!t) return false
  // Backends sometimes return their JSON schema literal "string".
  if (t === 'string') return false
  // Or known error/warning placeholders.
  if (/^(MYMEMORY WARNING|QUERY LENGTH|INVALID|PLEASE|NO QUERY|UNDEFINED)/i.test(t)) return false
  // Avoid no-ops (translation came back identical to source - only valid
  // if source was also one word and language is similar; we still let
  // single-word identicals pass through unchanged so we don't drop them
  // entirely, but treat them as low-confidence by returning null only
  // when we suspect schema-leak - already covered above).
  return true
}

function decodeEntities(s) {
  if (!s || s.indexOf('&') === -1) return s
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${s}`, 'text/html')
  return doc.body.textContent || s
}

async function tryLingva(text, source, target, signal) {
  const src = LINGVA_LANG[source] || source
  const dst = LINGVA_LANG[target] || target
  for (const base of LINGVA_MIRRORS) {
    try {
      const url = `${base}/api/v1/${src}/${dst}/${encodeURIComponent(text)}`
      const res = await fetch(url, { signal, headers: { Accept: 'application/json' } })
      if (!res.ok) continue
      const data = await res.json()
      if (data && isValidTranslation(data.translation, text)) {
        // Lingva (like MyMemory) can echo HTML entities (&nbsp;, &middot;,
        // &amp;) as literal text; decode them so the DOM never shows raw
        // entity strings on translated pages.
        return decodeEntities(data.translation)
      }
    } catch (_) { /* next mirror */ }
  }
  return null
}

async function tryMyMemory(text, source, target, signal) {
  try {
    const src = MYMEMORY_LANG[source] || source
    const dst = MYMEMORY_LANG[target] || target
    const url =
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}` +
      `&langpair=${encodeURIComponent(src + '|' + dst)}`
    const res = await fetch(url, { signal, headers: { Accept: 'application/json' } })
    if (!res.ok) return null
    const data = await res.json()
    if (data?.quotaFinished) return null
    const t = data?.responseData?.translatedText
    if (isValidTranslation(t, text)) return decodeEntities(t)
  } catch (_) {}
  return null
}

/**
 * Translate a single string. Returns the original on total failure so
 * the UI never goes blank.
 *
 * If every backend rejects a chunk (rate-limit, schema-leak, etc.) we
 * intentionally do NOT cache that chunk's result. That way subsequent
 * retranslate passes get another chance to bring the section into the
 * chosen language instead of forever showing English.
 */
export async function translateText(text, target, { source = 'en', signal } = {}) {
  if (!text || !text.trim()) return text
  if (source === target) return text
  const key = cacheKey(text, source, target)
  if (cache.has(key)) return cache.get(key)

  const chunks = chunkText(text, 480)
  const out = []
  let anyChunkSucceeded = false
  let everyChunkSucceeded = true
  for (const chunk of chunks) {
    let translated = await tryLingva(chunk, source, target, signal)
    if (!translated) translated = await tryMyMemory(chunk, source, target, signal)
    if (translated) {
      anyChunkSucceeded = true
      out.push(translated)
    } else {
      everyChunkSucceeded = false
      out.push(chunk)
    }
  }
  const result = out.join('')
  // Only cache when ALL chunks succeeded - partial failures leave the
  // door open for a retranslate pass to fill in the gaps.
  if (everyChunkSucceeded) cache.set(key, result)
  return anyChunkSucceeded || everyChunkSucceeded ? result : text
}

/**
 * Translate many strings with a bounded concurrency. Order preserved.
 */
export async function translateMany(
  texts,
  target,
  { source = 'en', concurrency = 4, signal, onProgress } = {}
) {
  const results = new Array(texts.length)
  let cursor = 0
  let done = 0
  async function worker() {
    while (cursor < texts.length) {
      const i = cursor++
      try {
        results[i] = await translateText(texts[i], target, { source, signal })
      } catch (_) {
        results[i] = texts[i]
      }
      done++
      onProgress && onProgress(done, texts.length)
    }
  }
  const workers = Array.from(
    { length: Math.min(concurrency, texts.length) },
    worker
  )
  await Promise.all(workers)
  return results
}

function chunkText(text, max) {
  if (text.length <= max) return [text]
  const parts = []
  let buf = ''
  const pieces = text.split(/(?<=[.!?。！？\n])\s+/)
  for (const piece of pieces) {
    if ((buf + piece).length > max && buf) {
      parts.push(buf)
      buf = ''
    }
    if (piece.length > max) {
      for (let i = 0; i < piece.length; i += max) parts.push(piece.slice(i, i + max))
    } else {
      buf += (buf ? ' ' : '') + piece
    }
  }
  if (buf) parts.push(buf)
  return parts
}
